import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Users, Bed, Bath, Star, Search, Filter } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function PropertyListing() {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    city: '',
    min_price: '',
    max_price: '',
    guests: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchProperties();
  }, []);

  const fetchProperties = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      if (filters.city) params.append('city', filters.city);
      if (filters.min_price) params.append('min_price', filters.min_price);
      if (filters.max_price) params.append('max_price', filters.max_price);
      if (filters.guests) params.append('guests', filters.guests);

      const response = await axios.get(`${API}/properties?${params.toString()}`);
      setProperties(response.data);
    } catch (error) {
      console.error('Failed to fetch properties:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value
    });
  };

  const handleSearch = () => {
    fetchProperties();
  };

  const clearFilters = () => {
    setFilters({
      city: '',
      min_price: '',
      max_price: '',
      guests: ''
    });
    setTimeout(() => fetchProperties(), 100);
  };

  return (
    <div style={{ padding: '40px 0', minHeight: 'calc(100vh - 70px)', background: '#f7fafc' }}>
      <div className="container">
        {/* Header */}
        <div style={{ marginBottom: '40px', textAlign: 'center' }}>
          <h1 style={{ 
            fontSize: '3rem', 
            fontWeight: '700', 
            color: '#2d3748',
            marginBottom: '16px'
          }}>
            Discover Amazing Properties
          </h1>
          <p style={{ 
            fontSize: '1.25rem', 
            color: '#718096',
            maxWidth: '600px',
            margin: '0 auto'
          }}>
            Find the perfect vacation rental for your last-minute getaway
          </p>
        </div>

        {/* Search and Filters */}
        <div style={{ 
          background: 'white', 
          padding: '32px', 
          borderRadius: '16px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
          marginBottom: '40px'
        }}>
          <div style={{ 
            display: 'flex', 
            gap: '16px', 
            alignItems: 'center',
            flexWrap: 'wrap'
          }}>
            <div style={{ flex: '1', minWidth: '250px' }}>
              <input
                type="text"
                name="city"
                placeholder="Search by city..."
                value={filters.city}
                onChange={handleFilterChange}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '10px',
                  fontSize: '16px'
                }}
              />
            </div>
            
            <button 
              onClick={() => setShowFilters(!showFilters)}
              className="btn btn-outline"
              style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <Filter size={20} />
              Filters
            </button>
            
            <button 
              onClick={handleSearch}
              className="btn btn-primary"
              style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <Search size={20} />
              Search
            </button>
          </div>

          {showFilters && (
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
              gap: '16px',
              marginTop: '24px',
              paddingTop: '24px',
              borderTop: '1px solid #e2e8f0'
            }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#4a5568' }}>
                  Min Price per Night
                </label>
                <input
                  type="number"
                  name="min_price"
                  placeholder="$0"
                  value={filters.min_price}
                  onChange={handleFilterChange}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '8px'
                  }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#4a5568' }}>
                  Max Price per Night
                </label>
                <input
                  type="number"
                  name="max_price"
                  placeholder="$1000"
                  value={filters.max_price}
                  onChange={handleFilterChange}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '8px'
                  }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#4a5568' }}>
                  Number of Guests
                </label>
                <select
                  name="guests"
                  value={filters.guests}
                  onChange={handleFilterChange}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '8px',
                    background: 'white'
                  }}
                >
                  <option value="">Any</option>
                  <option value="1">1 Guest</option>
                  <option value="2">2 Guests</option>
                  <option value="3">3 Guests</option>
                  <option value="4">4 Guests</option>
                  <option value="5">5+ Guests</option>
                </select>
              </div>
              
              <div style={{ display: 'flex', alignItems: 'end' }}>
                <button 
                  onClick={clearFilters}
                  style={{
                    padding: '10px 20px',
                    background: 'transparent',
                    color: '#667eea',
                    border: '1px solid #667eea',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Clear Filters
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Properties Grid */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px' }}>
            <div className="loading-spinner"></div>
            <p style={{ marginTop: '20px', color: '#718096' }}>Loading properties...</p>
          </div>
        ) : (
          <>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '24px'
            }}>
              <p style={{ color: '#718096' }}>
                {properties.length} propert{properties.length !== 1 ? 'ies' : 'y'} found
              </p>
            </div>

            {properties.length === 0 ? (
              <div style={{ 
                textAlign: 'center', 
                padding: '60px',
                background: 'white',
                borderRadius: '16px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
              }}>
                <MapPin size={48} style={{ margin: '0 auto 16px', opacity: 0.5, color: '#718096' }} />
                <h3 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '8px', color: '#2d3748' }}>
                  No properties found
                </h3>
                <p style={{ color: '#718096', marginBottom: '24px' }}>
                  Try adjusting your search criteria or check back later for new listings.
                </p>
                <button onClick={clearFilters} className="btn btn-primary">
                  Clear Filters
                </button>
              </div>
            ) : (
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', 
                gap: '24px' 
              }}>
                {properties.map((property) => (
                  <Link 
                    key={property.id} 
                    to={`/properties/${property.id}`}
                    style={{ textDecoration: 'none', color: 'inherit' }}
                  >
                    <div style={{
                      background: 'white',
                      borderRadius: '16px',
                      overflow: 'hidden',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                      transition: 'all 0.3s ease',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      cursor: 'pointer'
                    }} 
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'translateY(-4px)';
                      e.target.style.boxShadow = '0 12px 30px rgba(0, 0, 0, 0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.05)';
                    }}>
                      {/* Property Image Placeholder */}
                      <div style={{ 
                        height: '250px', 
                        background: 'linear-gradient(135deg, #667eea, #764ba2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '4rem',
                        position: 'relative'
                      }}>
                        🏠
                        <div style={{
                          position: 'absolute',
                          top: '16px',
                          right: '16px',
                          background: 'rgba(255, 255, 255, 0.9)',
                          color: '#2d3748',
                          padding: '4px 8px',
                          borderRadius: '12px',
                          fontSize: '0.875rem',
                          fontWeight: '600',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}>
                          <Star size={16} fill="#ffd700" color="#ffd700" />
                          {property.average_rating > 0 ? property.average_rating.toFixed(1) : 'New'}
                        </div>
                      </div>
                      
                      {/* Property Details */}
                      <div style={{ padding: '24px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px' }}>
                          <h3 style={{ 
                            fontSize: '1.25rem', 
                            fontWeight: '600', 
                            color: '#2d3748',
                            lineHeight: '1.3',
                            margin: 0
                          }}>
                            {property.title}
                          </h3>
                        </div>
                        
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px', color: '#718096' }}>
                          <MapPin size={16} style={{ marginRight: '6px' }} />
                          <span style={{ fontSize: '0.875rem' }}>
                            {property.city}, {property.state}, {property.country}
                          </span>
                        </div>
                        
                        <p style={{ 
                          color: '#718096', 
                          fontSize: '0.875rem', 
                          lineHeight: '1.5',
                          marginBottom: '16px',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden'
                        }}>
                          {property.description}
                        </p>
                        
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          paddingTop: '16px',
                          borderTop: '1px solid #e2e8f0'
                        }}>
                          <div style={{ display: 'flex', gap: '16px', fontSize: '0.875rem', color: '#718096' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <Bed size={16} />
                              {property.bedrooms}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <Bath size={16} />
                              {property.bathrooms}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <Users size={16} />
                              {property.max_guests}
                            </div>
                          </div>
                          
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ 
                              fontSize: '1.5rem', 
                              fontWeight: '700', 
                              color: '#667eea'
                            }}>
                              ${property.price_per_night}
                            </div>
                            <div style={{ fontSize: '0.875rem', color: '#718096' }}>
                              per night
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default PropertyListing;